import {
    shouldBeDisplayed,
    shouldBeEnabled,
    shouldUpdateRelatedField,
} from '@mfe-helpers/conditions';
import { setFieldOptions } from '@mfe-helpers/fieldOptions';
import {
    getErrorTranslation,
    getFieldTranslations,
    getOptionsTranslations,
} from '@mfe-helpers/fieldTranslations';
import { getIncrementValues } from '@mfe-helpers/rangeValue';
import type { Field, ICustomValue } from '@mfe-types/field';
import { IRelatedFieldToFormat } from '@mfe-types/field';
import { IStep } from '@mfe-types/form';
import { IReactHookFormErrorContent } from '@mfe-types/validator';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from '@aviv-unified-frontend/runtime/i18n';
import { NAME_SPACE } from '@mfe-presentation/hooks/useLoadTranslations';

interface IUseFieldUtils {
    watcher: FieldValues;
    field: Field;
    name: string;
    validationError?: IReactHookFormErrorContent;
    customValues?: ICustomValue[];
    config: IStep;
    translationParams?: Record<string, any>;
}

const getRelatedFieldToFormat = (
    config: IStep,
    relatedFieldToFormat: IRelatedFieldToFormat,
    formValues: Record<string, any>,
) => {
    const { fieldName, stepName } = relatedFieldToFormat;
    if (fieldName && stepName && config?.[stepName] && formValues) {
        const relatedField = config[stepName].getFields(formValues)[fieldName];
        const incrementalValues = getIncrementValues(relatedField, formValues);

        if (formValues[fieldName] === undefined || formValues[fieldName] === null) return;

        const formattedValue = relatedField.formatInputValue(
            relatedField,
            formValues[fieldName] + '',
            formValues,
            incrementalValues,
        );
        return { ...relatedField, fieldName, formattedValue };
    }
    return;
};

const useFieldUtils = ({
    field,
    name,
    validationError,
    watcher,
    config,
    translationParams,
}: IUseFieldUtils) => {
    const { t } = useTranslation(NAME_SPACE);

    const isDisplayed = shouldBeDisplayed(watcher, field.displayedConditions);
    const isDisabled = !shouldBeEnabled(watcher, field.enabledConditions);

    const fieldOptions = setFieldOptions(field, watcher);
    const optionsWithValuesTranslations = fieldOptions
        ? {
              ...fieldOptions,
              values: getOptionsTranslations(name, t, fieldOptions),
          }
        : null;

    const translations = getFieldTranslations({
        name,
        context: watcher?.estateType,
        isRequired: field.isRequired,
        isRequiredHidden: field?.isRequiredHidden,
        customValues: field?.customValues,
        translationParams,
        t,
    });

    const error = getErrorTranslation(name, t, validationError);

    return {
        isDisplayed,
        isDisabled,
        options: optionsWithValuesTranslations,
        translations,
        error,
        relatedFieldToUpdate:
            field?.relatedFieldToUpdate?.fieldName &&
            shouldUpdateRelatedField(watcher, field.relatedFieldToUpdate, name)
                ? field?.relatedFieldToUpdate
                : undefined,
        relatedFieldToFormat:
            field.relatedFieldToFormat &&
            getRelatedFieldToFormat(config, field.relatedFieldToFormat, watcher),
        relatedFieldToValidate: field?.relatedFieldToValidate,
    };
};

export default useFieldUtils;
